import { TextField, FunctionField, useGetMany, usePermissions, useRecordContext, useTranslate } from 'react-admin';
import { CampaignTileImage } from '@/components/adsCampaings/CampaignTileImage.jsx';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette';
import { CustomButton } from '@/components/global/buttons/CustomButton.jsx';
import EditIcon from '@/assets/icons/edit-icon.svg?react';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { AdsCampaignsPeriodItem } from '@/components/adsCampaings/AdsCampaignsPeriodItem.jsx';
import { marketingStyles } from '@/themes/commonStyles/marketing';
import { isAdStatusEditable, isAdStatusEnded } from '@/constants/AdStatus.js';
import CopyIcon from '@/assets/icons/copy-color-icon.svg?react';
import { useDrawer } from '@/composables/useDrawer';
import { canViewDetails } from '@/helpers/permissions/canViewDetails.js';
import { AdsCampaignMoreActions } from '@/components/adsCampaings/AdsCampaignMoreActions.jsx';

const StyledAdsCampaingsTile = styled(Box)(() => marketingStyles.adsCampaingsTile);

const formatTimeLabel = time => {
    if (!time) {
        return '';
    }

    return time.length >= 5 ? time.slice(0, 5) : time;
};

const formatDisplayHours = (record, translate) => {
    if (record?.isContinuous) {
        return translate('resources.adsCampaign.labels.continuous');
    }

    const startTime = formatTimeLabel(record?.startTime);
    const endTime = formatTimeLabel(record?.endTime);

    if (startTime && endTime) {
        return `${translate('resources.adsCampaign.labels.from')} ${startTime} ${translate('resources.adsCampaign.labels.to')} ${endTime}`;
    }

    if (startTime || endTime) {
        return startTime || endTime;
    }

    return translate('resources.adsCampaign.labels.noLimits');
};

export const AdsCampaignDisplayHoursField = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    return <Typography variant='body1'>{formatDisplayHours(record, translate)}</Typography>;
};

const getEmbeddedDeviceGroupNames = record => {
    const groups = record?.deviceGroups;
    if (!Array.isArray(groups) || groups.length === 0) {
        return null;
    }

    const names = groups.map(group => group?.name).filter(Boolean);
    return names.length > 0 ? names.join(', ') : null;
};

export const AdsCampaignDeviceGroupsField = () => {
    const record = useRecordContext();
    const embeddedNames = getEmbeddedDeviceGroupNames(record);
    const deviceGroupIds = record?.deviceGroupIds || [];
    const shouldFetchNames = !embeddedNames && deviceGroupIds.length > 0;
    const { data, isPending } = useGetMany('device-group', { ids: deviceGroupIds }, { enabled: shouldFetchNames });

    if (embeddedNames) {
        return <Typography variant='body1'>{embeddedNames}</Typography>;
    }

    if (shouldFetchNames && isPending) {
        return <Typography variant='body1'>—</Typography>;
    }

    if (shouldFetchNames && data?.length) {
        const names = data.map(group => group.name).filter(Boolean);
        return <Typography variant='body1'>{names.length > 0 ? names.join(', ') : '—'}</Typography>;
    }

    return <Typography variant='body1'>—</Typography>;
};

export const AdsCampaingsTile = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const record = useRecordContext();
    const { openDrawer } = useDrawer();
    const { permissions } = usePermissions();
    const isFinished = isAdStatusEnded(record?.status);
    const canManageAd = (permissions?.isAdmin || permissions?.isClient) && !permissions?.isPosOwner;
    const canEditAd = canManageAd && isAdStatusEditable(record?.status);
    const canShowAd = canViewDetails('ad', permissions);
    const showCopyAction = canManageAd && isFinished;
    const showEditAction = canEditAd;
    const showMoreActions = canShowAd || canManageAd;
    const showPrimaryAction = showEditAction || showCopyAction;
    const actionLabelKey = isFinished ? 'copy' : 'edit';
    const actionLabel = isMobile ? '' : translate(`resources.adsCampaign.labels.${actionLabelKey}`);
    const actionIcon = isFinished ? <CopyIcon /> : <EditIcon />;

    const handleActionClick = event => {
        event.stopPropagation();
        if (showEditAction) {
            openDrawer(record.id, 'ad-edit');
        }
    };

    return (
        <StyledAdsCampaingsTile>
            <Box className='tile-image'>
                <Box>
                    <CampaignTileImage
                        opacity={isFinished ? 0.5 : 1}
                        sx={{
                            width: '160px',
                            height: '284px',
                            borderRadius: '12px',
                            border: `2px solid ${Palette.gray.gray100}`,
                            margin: '0 0 20px 0',
                            [`@media (max-width: ${breakpoints.values.xl}px)`]: {
                                width: '120px',
                                height: '213px',
                            },
                            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                width: '64px',
                                height: '113px',
                                margin: '0',
                            },
                        }}
                    />
                </Box>
                {(showPrimaryAction || showMoreActions) && (
                    <Box className='tile-actions' sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {showPrimaryAction ? (
                            <CustomButton
                                color='secondary'
                                startIcon={actionIcon}
                                label={actionLabel}
                                disabled={showCopyAction}
                                onClick={showEditAction ? handleActionClick : undefined}
                                sx={{ flex: 1 }}
                            />
                        ) : null}
                        {showMoreActions ? <AdsCampaignMoreActions /> : null}
                    </Box>
                )}
            </Box>
            <Box className='tile-content' sx={{ opacity: isFinished ? 0.7 : 1 }}>
                <TextField source='name' variant='h5' />
                <FunctionField
                    label={translate('resources.adsCampaign.labels.period')}
                    source='record'
                    render={record => <AdsCampaignsPeriodItem record={record} />}
                />
                <Box className='tile-content-item'>
                    <Typography variant='caption'>{translate('resources.adsCampaign.labels.displayHours')}</Typography>
                    <AdsCampaignDisplayHoursField />
                </Box>
                <Box className='tile-content-item'>
                    <Typography variant='caption'>{translate('resources.adsCampaign.labels.group')}</Typography>
                    <AdsCampaignDeviceGroupsField />
                </Box>
            </Box>
        </StyledAdsCampaingsTile>
    );
};
